import { useEffect, useMemo, useState } from 'react';
import { getWindow } from '@hzdg/dom-utils';

import { useGetDayPerformancesQuery } from '../services/viva';
import {
  actions,
  selectCheckoutCartAddonSelections,
  selectCheckoutCartSelectionsByDateTime,
  selectWizardCurrentStep,
  useAppDispatch,
  useAppSelector,
} from '../store';

import type { Flow } from '../services/viva/constants';

export const useGetEditQueryParams = () => {
  const window = getWindow();
  const urlSearch = new URLSearchParams(window?.location.search);
  const editMode =
    urlSearch.get('edit') === null ? false : Boolean(urlSearch.get('edit'));
  const originalDateTime = parseInt(urlSearch.get('date') ?? '-1');
  const flow = urlSearch.get('flow') as Flow | null;

  return { editMode, originalDateTime, flow };
};

export const getIsBuyTicketPage = () => {
  const window = getWindow();

  return window?.location.pathname.includes('buy-tickets') || false;
};

export const useTicketEditMode = (flow: Flow) => {
  const {
    editMode,
    originalDateTime: editDate,
    flow: urlFlow,
  } = useGetEditQueryParams();
  const { data: availableDays } = useGetDayPerformancesQuery({
    date: new Date(editDate).toISOString().split('T')[0],
    options: {
      enabled: editDate !== -1,
    },
  });

  const dispatch = useAppDispatch();

  const cartSelections = useAppSelector(
    (state) => state.checkout.cart.selections,
  );
  const selectionsForFlow = cartSelections[flow] ?? [];

  const selectionsByFlowAndDate = useAppSelector((state) =>
    selectCheckoutCartSelectionsByDateTime(state, flow, editDate),
  );
  const selectionsByFlow = useAppSelector((state) =>
    selectCheckoutCartSelectionsByDateTime(state, flow),
  );

  const selections = useMemo(() => {
    if (selectionsForFlow.length > 0) {
      return selectionsForFlow;
    }
    return selectionsByFlowAndDate.length
      ? selectionsByFlowAndDate
      : selectionsByFlow;
  }, [selectionsForFlow, selectionsByFlowAndDate, selectionsByFlow]);

  const shopCartSelections = useAppSelector(
    (state) => state.order.shopCart.selections,
  );

  const addOnSelections = useAppSelector(selectCheckoutCartAddonSelections);
  const currentStep = useAppSelector(selectWizardCurrentStep);
  const [isInitialRender, setIsInitialRender] = useState(true);

  useEffect(() => {
    const window = getWindow();
    if (!window) return;

    // If we have selections but no edit mode, set the URL parameters
    if (selections?.length && editMode === false) {
      const url = new URL(window.location.href);
      url.searchParams.set('edit', 'true');
      url.searchParams.set('date', selections[0]?.dateTime?.toString() ?? '');
      url.searchParams.set('flow', flow);
      window.history.replaceState({}, document.title, url.toString());
      return;
    }

    if (!isInitialRender) return;
    if (!editMode || !editDate || !availableDays) {
      return;
    }

    if (!selections?.length) {
      // No selections, remove params
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    dispatch(
      actions.order.setShopCartFlowForEditing({
        flow,
        data: selections,
        addOns: addOnSelections[flow][editDate] ?? [],
      }),
    );

    dispatch(actions.order.setShopCartDateTime(editDate));

    const date = new Date(editDate);
    const dateObj = {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
      time: date.getTime(),
    };

    dispatch(actions.order.setShopCartDate(dateObj));
    dispatch(actions.wizard.setSelectedDatePicker(dateObj));

    // Update the checkout cart selections after original selection has been changed
    if (currentStep >= 2) {
      dispatch(
        actions.checkout.updateCheckoutCartSelections({
          flow,
          dateTime: editDate,
          selections: shopCartSelections,
        }),
      );
    }

    setIsInitialRender(false);
  }, [
    isInitialRender,
    editDate,
    editMode,
    availableDays,
    currentStep,
    selections,
    flow,
    urlFlow,
  ]);
};
